// HuntingWindowCard.jsx
// Single dashboard plan-status card.
// Covers:
// - Smart Timing active and watching
// - Smart Timing buy already complete for current window
// - Standard DCA active

import React, { useEffect, useState } from "react";

/* ─── helpers ─────────────────────────────────────────────────────────────── */

function parseApiDateUtc(value) {
  if (!value) return null;

  if (value instanceof Date) return value;

  if (typeof value !== "string") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const s = value.trim();

  const hasTimezone =
    /Z$/i.test(s) ||
    /[+-]\d{2}:\d{2}$/.test(s) ||
    /[+-]\d{4}$/.test(s);

  const d = new Date(hasTimezone ? s : `${s}Z`);

  return Number.isNaN(d.getTime()) ? null : d;
}

function windowDays(freq) {
  if (!freq) return 14;

  const f = String(freq).toLowerCase();

  if (f === "daily") return 1;
  if (f === "weekly") return 7;

  return 14; // bi-weekly default
}

function nextWindowStart(freq, windowStartIso) {
  const start = parseApiDateUtc(windowStartIso);
  if (!start || !freq) return null;

  const d = new Date(start);
  const f = String(freq).toLowerCase();

  if (f === "daily") d.setDate(d.getDate() + 1);
  else if (f === "weekly") d.setDate(d.getDate() + 7);
  else d.setDate(d.getDate() + 14);

  return d;
}

function fmtDate(dt) {
  if (!dt) return "—";

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(dt);
}

function formatFiat(amount, currency = "USD") {
  const value = Number(amount);
  const safeCurrency = String(currency || "USD").toUpperCase();

  if (!Number.isFinite(value)) {
    return "—";
  }

  if (safeCurrency === "USDT") {
    return `${value.toFixed(2)} USDT`;
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: safeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${safeCurrency} ${value.toFixed(2)}`;
  }
}

function fmtSats(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${Math.round(v).toLocaleString()} sats`;
}

function prettyFreq(freq) {
  if (!freq) return "bi-weekly";
  return String(freq).replace("_", "-");
}

function normalizeTranches(rawTranches) {
  if (Array.isArray(rawTranches)) {
    return rawTranches;
  }

  if (typeof rawTranches === "string") {
    try {
      const parsed = JSON.parse(rawTranches);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

function getRemainingFiat(plan) {
  const raw =
    plan?.unspent_fiat ??
    plan?.unspent_fiat_usd ??
    plan?.amount_fiat ??
    plan?.amount_usd ??
    0;

  const value = Number(raw);

  return Number.isFinite(value)
    ? Math.max(0, value)
    : 0;
}

/* ─── small UI pieces ─────────────────────────────────────────────────────── */

function PulseDot({ color = "bg-orange-500" }) {
  return (
    <span className="relative inline-flex h-2.5 w-2.5">
      <span
        className={`animate-ping absolute inline-flex h-full w-full rounded-full ${color} opacity-60`}
      />
      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${color}`} />
    </span>
  );
}

function StaticDot({ color = "bg-emerald-500" }) {
  return <span className={`inline-flex h-2.5 w-2.5 rounded-full ${color}`} />;
}

function WindowRing({
  windowStartIso,
  freq,
  targetDate,
  label = "remaining",
  mode = "window-progress",
}) {
  const totalMs = windowDays(freq) * 24 * 3600 * 1000;
  const [valueMs, setValueMs] = useState(0);

  useEffect(() => {
    const tick = () => {
      if (mode === "countdown" && targetDate) {
        setValueMs(Math.max(targetDate.getTime() - Date.now(), 0));
        return;
      }

      const start = windowStartIso
        ? parseApiDateUtc(windowStartIso)?.getTime()
        : Date.now();

      setValueMs(Math.min(Math.max(Date.now() - start, 0), totalMs));
    };

    tick();

    const id = setInterval(tick, 60_000);

    return () => clearInterval(id);
  }, [windowStartIso, totalMs, targetDate, mode]);

  const pct =
    mode === "countdown"
      ? Math.max(0, Math.min(1, 1 - valueMs / totalMs))
      : Math.max(0, Math.min(1, valueMs / totalMs));

  const days =
    mode === "countdown"
      ? Math.max(0, Math.ceil(valueMs / (24 * 3600 * 1000)))
      : Math.max(0, Math.ceil((totalMs - valueMs) / (24 * 3600 * 1000)));

  const R = 20;
  const CX = 24;
  const CY = 24;
  const circ = 2 * Math.PI * R;
  const dash = circ * pct;

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true">
        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="none"
          stroke="#27272a"
          strokeWidth="4"
        />

        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="none"
          stroke="#f97316"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`}
          transform={`rotate(-90 ${CX} ${CY})`}
          style={{ transition: "stroke-dasharray 600ms ease" }}
        />

        <text
          x={CX}
          y={CY + 4}
          textAnchor="middle"
          fontSize="10"
          fontWeight="600"
          fill="#f97316"
          fontFamily="monospace"
        >
          {days}d
        </text>
      </svg>

      <span className="text-[10px] text-zinc-500 leading-none">
        {label}
      </span>
    </div>
  );
}

function WhyTooltip() {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] text-zinc-500 underline decoration-dotted hover:text-zinc-300 transition"
        aria-expanded={open}
      >
        Why didn't it buy yet?
      </button>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-2 w-64 bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-xs text-zinc-300 shadow-xl z-10"
          role="tooltip"
        >
          <p className="font-semibold text-white mb-1">
            Smart Timing is working 🔍
          </p>

          <p>
            Instead of buying the moment your window opens, SatStacker watches
            for a dip during your active window. If no dip appears, it will
            still buy before the window closes.
          </p>

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-2 text-zinc-500 hover:text-zinc-300"
          >
            Got it ✕
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── main card ───────────────────────────────────────────────────────────── */

export default function HuntingWindowCard({
  plan,
  completedWindow = null,
  onManagePlan,
  smartTimingNudgeDismissed = false,
  onDismissSmartTimingNudge,
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  if (!plan) return null;

  const now = new Date();

  const freq = prettyFreq(plan.freq || "bi-weekly");

  const fiatCurrency = String(
    plan.fiat_currency || "USD"
  )
    .trim()
    .toUpperCase();

  const amountFiatRaw = Number(
    plan.amount_fiat ??
    plan.amount_usd ??
    0
  );

  const amountFiat = Number.isFinite(amountFiatRaw)
    ? Math.max(0, amountFiatRaw)
    : 0;

  const remainingFiat = getRemainingFiat(plan);

  const windowStart = parseApiDateUtc(
    plan.window_start
  );

  const windowEnd = windowStart
    ? new Date(
        windowStart.getTime() +
        windowDays(freq) * 24 * 3600 * 1000
      )
    : null;

  const nextWindow = nextWindowStart(
    freq,
    plan.window_start
  );

  const nextRun = parseApiDateUtc(
    plan.next_run
  );

  const smartTiming = Boolean(
    plan.smart_timing
  );

  const standardDca = !smartTiming;

  const tranches = normalizeTranches(
    plan.tranches
  );

  const allTranchesExecuted =
    tranches.length > 0 &&
    tranches.every(
      (tranche) => tranche?.exec === true
    );

  const noRemainingAllocation =
    remainingFiat <= 0.005;

  const currentWindowOpen = Boolean(
    windowStart &&
    windowEnd &&
    now >= windowStart &&
    now < windowEnd
  );

  const currentWindowComplete =
    allTranchesExecuted ||
    noRemainingAllocation;

  const waitingForWindowStart = Boolean(
    windowStart &&
    now < windowStart
  );

  const completedBeforeNextWindow = Boolean(
    currentWindowComplete &&
    nextWindow &&
    now < nextWindow
  );

  const smartBetweenWindows =
    smartTiming &&
    (
      waitingForWindowStart ||
      completedBeforeNextWindow
    );

  const upcomingWindowStart =
    waitingForWindowStart
      ? windowStart
      : nextWindow && nextWindow > now
        ? nextWindow
        : nextRun;

  const smartWindowActive =
    smartTiming &&
    currentWindowOpen &&
    !currentWindowComplete;

  const nextStandardBuy =
    nextRun && nextRun > now
      ? nextRun
      : nextWindow || windowStart;

  let state;

  if (standardDca) {
    state = {
      title: "Standard DCA active",
      pill: "Active",
      dot: (
        <StaticDot color="bg-emerald-500" />
      ),
      pillClass:
        "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      body:
        "Your Bitcoin buys run automatically on your schedule.",
    };
  } else if (smartBetweenWindows) {
    state = {
      title: currentWindowComplete
        ? "Smart Timing buy complete"
        : "Next Smart Timing window",
      pill: "Waiting",
      dot: (
        <StaticDot color="bg-orange-500" />
      ),
      pillClass:
        "bg-orange-500/10 text-orange-400 border-orange-500/30",
      body: currentWindowComplete
        ? "This window’s buy is complete. Smart Timing will start watching again when your next window begins."
        : "Your next Smart Timing window has not started yet. We’ll begin watching automatically when it opens.",
    };
  } else if (smartWindowActive) {
    state = {
      title: "Smart Timing window active",
      pill: "Active",
      dot: <PulseDot />,
      pillClass:
        "bg-orange-500/10 text-orange-400 border-orange-500/30",
      body:
        "Your Smart Timing window is open. We're watching Bitcoin's price and will buy when we see a dip. If no dip appears, it buys before the window closes.",
    };
  } else {
    state = {
      title: "Smart Timing scheduled",
      pill: "Scheduled",
      dot: (
        <StaticDot color="bg-orange-500" />
      ),
      pillClass:
        "bg-orange-500/10 text-orange-400 border-orange-500/30",
      body:
        "Your Smart Timing plan is scheduled. We’ll start watching during your next active window.",
    };
  }

  return (
    <div
      className={`
        mb-6 max-w-lg w-full bg-card rounded-xl border border-border shadow-card
        overflow-hidden
        transition-all duration-500
        ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}
      `}
    >
      {/* top accent bar */}
      <div className="h-[3px] w-full bg-zinc-800 relative overflow-hidden">
        {smartTiming && (
          <div
            className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-orange-500 to-transparent"
            style={{
              animation: smartWindowActive
                ? "shimmer 2.4s ease-in-out infinite"
                : "none",
              transform: smartWindowActive ? undefined : "translateX(50%)",
            }}
          />
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
      `}</style>

      <div className="p-4">
        {/* header row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {state.dot}
            <span className="text-sm font-semibold text-white tracking-tight">
              {state.title}
            </span>
          </div>

          <span
            className={`text-[11px] px-2 py-1 rounded-full border ${state.pillClass}`}
          >
            {state.pill}
          </span>
        </div>

        {/* main message */}
        <p className="text-sm text-zinc-300 leading-snug mb-4">
          {state.body}
        </p>

        {/* Smart Timing active layout */}
        {smartWindowActive && (
          <div className="flex items-center gap-4">
            <WindowRing windowStartIso={plan.window_start} freq={freq} />

            <div className="flex-1 space-y-2.5">
              <div className="flex justify-between text-xs gap-3">
                <span className="text-zinc-500">Remaining</span>
                <span className="tabular-nums text-white font-medium text-right">
                  {formatFiat(remainingFiat, fiatCurrency)} of BTC
                </span>
              </div>

              <div className="flex justify-between text-xs gap-3">
                <span className="text-zinc-500">Window closes</span>
                <span className="tabular-nums text-zinc-300 text-right">
                  {fmtDate(windowEnd)}
                </span>
              </div>

              <div className="flex justify-between text-xs gap-3">
                <span className="text-zinc-500">If no dip found</span>
                <span className="text-zinc-300 text-right">
                  Buys at close anyway
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Smart Timing complete layout */}
        {smartTiming && !smartWindowActive && (
          <div className="flex items-center gap-4">
            <WindowRing
              windowStartIso={plan.window_start}
              freq={freq}
              targetDate={upcomingWindowStart}
              mode="countdown"
              label="until next"
            />

            <div className="flex-1 space-y-2.5">
              {completedWindow?.totalSats ? (
                <>
                  <div className="flex justify-between text-xs gap-3">
                    <span className="text-zinc-500">Bought</span>
                    <span className="tabular-nums text-white font-medium text-right">
                      {fmtSats(completedWindow.totalSats)}
                    </span>
                  </div>

                  <div className="flex justify-between text-xs gap-3">
                    <span className="text-zinc-500">Avg entry</span>

                    <span className="tabular-nums text-zinc-300 text-right">
                      {Number.isFinite(Number(completedWindow.avgBuyPrice))
                        ? `$${Number(completedWindow.avgBuyPrice).toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}`
                        : completedWindow.executionLabel || "Buy complete"}
                    </span>
                  </div>

                  <div className="flex justify-between text-xs gap-3">
                    <span className="text-zinc-500">Next window</span>
                    <span className="tabular-nums text-zinc-300 text-right">
                      {fmtDate(upcomingWindowStart)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-xs gap-3">
                    <span className="text-zinc-500">Plan</span>
                    <span className="tabular-nums text-white font-medium text-right">
                      {formatFiat(amountFiat, fiatCurrency)} {freq}
                    </span>
                  </div>

                  <div className="flex justify-between text-xs gap-3">
                    <span className="text-zinc-500">Next window</span>
                    <span className="tabular-nums text-zinc-300 text-right">
                      {fmtDate(upcomingWindowStart)}
                    </span>
                  </div>

                  <div className="flex justify-between text-xs gap-3">
                    <span className="text-zinc-500">Status</span>
                    <span className="text-zinc-300 text-right">
                      Watching resumes then
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Standard DCA layout */}
        {standardDca && (
          <div className="rounded-xl border border-border/70 bg-zinc-900/50 p-3 text-sm space-y-2">
            <div className="flex justify-between gap-3">
              <span className="text-zinc-500">Buy amount</span>
              <span className="text-white font-medium text-right">
                {formatFiat(amountFiat, fiatCurrency)} {freq}
              </span>
            </div>

            <div className="flex justify-between gap-3">
              <span className="text-zinc-500">Next buy</span>
              <span className="text-zinc-300 tabular-nums text-right">
                {fmtDate(nextStandardBuy)}
              </span>
            </div>

            <div className="flex justify-between gap-3">
              <span className="text-zinc-500">Timing</span>
              <span className="text-zinc-300 text-right">
                Fixed schedule
              </span>
            </div>
          </div>
        )}

        {/* Standard DCA Smart Timing nudge */}
        {standardDca && !smartTimingNudgeDismissed && (
          <div className="relative mt-3 rounded-lg bg-orange-500/10 border border-orange-500/25 px-3 py-2 pr-7">
            {onDismissSmartTimingNudge && (
              <button
                type="button"
                onClick={onDismissSmartTimingNudge}
                className="absolute top-1.5 right-2 text-orange-400/60 hover:text-orange-300 text-sm leading-none"
                aria-label="Dismiss"
              >
                ×
              </button>
            )}

            <button
              type="button"
              onClick={onManagePlan}
              className="flex items-center gap-2 text-left"
            >
              <span className="text-orange-400 text-base leading-none">⚡</span>
              <p className="text-xs text-orange-300 leading-snug">
                <span className="font-semibold">
                  Want better entries?
                </span>{" "}
                Turn on Smart Timing.
              </p>
            </button>
          </div>
        )}

        <div className="border-t border-border/50 mt-4 pt-3 flex items-center justify-between gap-3">
          {smartWindowActive ? (
            <WhyTooltip />
          ) : (
            <span className="text-[11px] text-zinc-600 tabular-nums uppercase tracking-wide whitespace-nowrap">
              {freq} plan
            </span>
          )}

          <button
            type="button"
            onClick={onManagePlan}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wide whitespace-nowrap"
          >
            Manage plan
          </button>
        </div>
      </div>
    </div>
  );
}